// Stat-band resolution shared by the homepage hero and the SEO landing pages.
// Client-safe: a pure function, no server imports.

export type StatDef = {
  value: number;
  suffix: string;
  decimals: number;
  label: string;
};

/**
 * Apply the CMS overlay to a page's default stat band.
 *
 * The value field accepts what an editor sees on the page — "11.5", "425+",
 * "9" — so anything trailing the number is taken as the suffix. Editing the
 * figure in place then does what it looks like: typing "450+" gives 450 with
 * the plus, typing "450" drops it. Decimals follow the number as typed, so
 * "11.50" keeps both places. The separate Suffix field still wins when filled.
 */
export function resolveStats(
  defaults: StatDef[],
  c: Record<string, string> = {},
): StatDef[] {
  return defaults.map((d, i) => {
    const raw = c[`stat${i + 1}.value`]?.trim();
    const num = raw?.match(/-?\d+(\.\d+)?/)?.[0];
    const value = num ? parseFloat(num) : d.value;
    const decimals = num ? (num.includes(".") ? num.split(".")[1].length : 0) : d.decimals;
    const rawSuffix = num ? raw!.slice(raw!.indexOf(num) + num.length).trim() : undefined;
    const explicitSuffix = c[`stat${i + 1}.suffix`]?.trim();
    return {
      value,
      decimals,
      suffix: explicitSuffix || rawSuffix || (raw ? "" : d.suffix),
      label: c[`stat${i + 1}.label`]?.trim() || d.label,
    };
  });
}

/**
 * The dated footnote under a stat band. An absent override keeps the page
 * default; an override saved empty hides the line entirely.
 */
export function resolveStatsNote(
  fallback: string,
  c: Record<string, string> = {},
): string {
  return c["stats.note"] === undefined ? fallback : c["stats.note"].trim();
}

/**
 * The home page hero's stat band — the site's canonical figures, verbatim from
 * the live site (auditor-approved IPO numbers).
 *
 * These live here rather than in components/sections/Hero.tsx because the blog
 * article template shows the same band, and the Figma comment on that design
 * is explicit: "The numbers will be same as website". Two copies would drift
 * the first time a figure moved.
 */
export const HOME_STATS: StatDef[] = [
  { value: 9, suffix: "", decimals: 0, label: "Cities" },
  { value: 11.46, suffix: "", decimals: 2, label: "Mn Sq Ft" },
  { value: 425, suffix: "+", decimals: 0, label: "Enterprise Clients" },
  { value: 80, suffix: "+", decimals: 0, label: "Centres" },
];

/** Dates the band so the figures read as a snapshot, not a live count. */
export const HOME_STATS_NOTE = "*As of March, 2026";

/**
 * Split a stat as an editor types it — "110+", "11.46M", "45" — into the
 * parts <Counter> needs. Same rules as resolveStats above, lifted out so a
 * page whose CMS keys differ (the GCC landing pages number theirs from 0 and
 * keep the unit in its own field) can animate its figures the way the home
 * hero does.
 *
 * Returns null when there is no number to count to, so a value like "Coming
 * soon" renders as written instead of animating to zero.
 */
export function parseStatFigure(
  raw: string | null | undefined,
): { value: number; suffix: string; decimals: number } | null {
  const text = (raw ?? "").trim();
  const num = text.match(/-?\d+(\.\d+)?/)?.[0];
  if (!num) return null;
  return {
    value: parseFloat(num),
    decimals: num.includes(".") ? num.split(".")[1].length : 0,
    suffix: text.slice(text.indexOf(num) + num.length).trim(),
  };
}
